import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';

export interface ProjectSummary {
  conversationId: string;
  title: string | null;
  koji: string;
  startedAt: number;
  lastTurnAt: number;
  turnCount: number;
  status: string;
  artifactCounts: {
    images: number;
    music: number;
    speech: number;
    captions: number;
    videos: number;
    // Stage 38: Stage 28 markdown documents tied to a turn.
    documents: number;
  };
}

export interface ProjectTurn {
  id: string;
  role: string;
  content: string;
  tool_name: string | null;
  tool_calls: string | null;
  tool_result: string | null;
  koji_codename: string;
  created_at: number;
  turn_index: number;
}

export interface ProjectImage {
  id: string;
  r2_key: string;
  title: string | null;
  prompt: string | null;
  aspect_ratio: string | null;
  mime_type: string | null;
  created_at: number;
  signedUrl: string;
}

export interface ProjectMusic {
  id: string;
  r2_key: string;
  title: string | null;
  prompt: string | null;
  model: string | null;
  format: string | null;
  duration_ms: number | null;
  created_at: number;
  signedUrl: string;
}

export interface ProjectSpeech {
  id: string;
  r2_key: string;
  title: string | null;
  voice: string | null;
  mode: string | null;
  duration_ms: number | null;
  created_at: number;
  signedUrl: string;
}

export interface ProjectCaption {
  id: string;
  source_r2_key: string | null;
  caption: string;
  created_at: number;
  signedUrl: string | null;
}

export interface ProjectUpload {
  id: string;
  r2_key: string;
  mime_type: string;
  size_bytes: number;
  turn_id: string | null;
  created_at: number;
  signedUrl: string;
}

export interface ProjectVideo {
  id: string;
  r2_key: string;
  title: string | null;
  prompt: string;
  tool_name: string;
  model: string;
  duration_seconds: number | null;
  aspect_ratio: string | null;
  resolution: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  koji_codename: string | null;
  created_at: number;
  signedUrl: string;
}

// Stage 38: write_markdown_file artifacts surfaced in chat-projects.
export interface ProjectDocument {
  id: string;
  r2_key: string;
  title: string;
  filename_slug: string;
  skill_name: string | null;
  size_bytes: number;
  created_at: number;
  signedUrl: string;
}

export interface ProjectDetail {
  conversation: {
    id: string;
    persona_id: string | null;
    title: string | null;
    koji_codename: string;
    started_at: number;
    last_turn_at: number;
    turn_count: number;
    status: string;
  };
  turns: ProjectTurn[];
  artifacts: {
    images: ProjectImage[];
    music: ProjectMusic[];
    speech: ProjectSpeech[];
    captions: ProjectCaption[];
    uploads?: ProjectUpload[];
    videos?: ProjectVideo[];
    // Stage 38. Optional for back-compat with cached responses from
    // pre-Stage-38 deployments.
    documents?: ProjectDocument[];
  };
}

export interface UseProjectsResult {
  list: ProjectSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadProject: (id: string) => Promise<ProjectDetail>;
  deleteProject: (id: string) => Promise<void>;
}

export function useProjects(): UseProjectsResult {
  const [list, setList] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ projects: ProjectSummary[] }>('/api/projects');
      setList(res.projects ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProject = useCallback(
    async (id: string): Promise<ProjectDetail> =>
      apiGet<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}`),
    [],
  );

  const deleteProject = useCallback(
    async (id: string): Promise<void> => {
      await apiJson<{ deleted: boolean; artifactsRemoved: number }>(
        'DELETE',
        `/api/projects/${encodeURIComponent(id)}`,
      );
      setList((prev) => prev.filter((p) => p.conversationId !== id));
    },
    [],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { list, loading, error, refresh, loadProject, deleteProject };
}
